import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { InvoiceValidationError } from "$routes/_lib/billing-v2/invoice-source";
import { randomUniqueId } from "$routes/_lib/random-id";
import { buildFinalisedInvoice } from "$routes/patient/[:patientId]/_lib/invoices-v2/build-finalised-invoice";
import { Box, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import { updateWithInsurerChargeIds } from "./update-with-insurer-charge-ids";

const useStyles = makeStyles((theme) => ({
  warningMessage: {
    color: theme.palette.grey[600],
    fontWeight: 400,
    marginLeft: theme.spacing(0.5),
  },
  warningIcon: { marginRight: theme.spacing(2) },
}));

/**
 * V2 finalise confirmation. Promotes the invoice from DRAFT → FINALIZED
 * via `doc.patch(...)`; replication propagates to the backend on next
 * sync. No apollo `finalizeInvoice` mutation.
 */
export const FinaliseInvoiceModalV2 = ({
  doc,
  open,
  onClose,
  flushPendingSaves,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
  /** Awaited before reading `balance.total`, so a pending debounced autosave
   * lands before we decide the finalised status. */
  flushPendingSaves?: () => void | Promise<void>;
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const [busy, setBusy] = useState(false);

  const handleFinalise = async () => {
    setBusy(true);
    try {
      await flushPendingSaves?.();
      const timestamp = getCurrentTime().toISOString();
      await updateWithInsurerChargeIds(doc, {
        actionedByUserId: user.id,
        timestamp,
        createTransactionId: randomUniqueId,
        update: (invoice, insurerCharges) =>
          buildFinalisedInvoice(invoice.toRecord(), {
            actionedByUserId: user.id,
            timestamp,
            insurerCharges,
          }),
      });
    } catch (err) {
      // Validation failures carry an actionable message (e.g. "Invoice
      // doesn't have items."); everything else gets the generic toast.
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          err instanceof InvoiceValidationError
            ? err.message
            : formatMessage({
                id: "invoicing.finaliseInvoice.error.msg",
                defaultMessage: "Failed to finalise this invoice",
              }),
        );
      }
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <DialogContainer open={open} onClose={onClose}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.confirmation.title"
              defaultMessage="Finalise Invoice?"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogBody>
        <Typography variant="h6" className={classes.warningMessage}>
          <FormattedMessage
            id="invoicing.confirmation.warning"
            defaultMessage="Once you finalise you will not be able to do further edits."
          />
        </Typography>
      </DialogBody>
      <DialogFooter>
        <Box>
          <Button color="secondary" onClick={onClose} disabled={busy}>
            <FormattedMessage
              id="invoicing.confirmation.cancel"
              defaultMessage="Cancel"
            />
          </Button>
        </Box>
        <Button
          onClick={handleFinalise}
          color="primary"
          data-testid="invoicing.confirmation.accept"
          startIcon={<CheckIcon />}
          loading={busy}
        >
          <FormattedMessage
            id="invoicing.confirmation.accept"
            defaultMessage="Finalise"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};
